/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import type { DataValue } from 'react-apollo';
import Waypoint from 'react-waypoint';

import { useQuery } from '@apollo/client';
import { reject, times, unionBy } from 'lodash';

import { Grid, InlineNotification, PageGridContainer, Typography2, breakpoints } from '@coursera/cds-core';

import Collection from 'bundles/enterprise-collections/components/Collection';
import { EnterpriseProductCardCollectionsPlaceholder } from 'bundles/enterprise-collections/components/EnterpriseProductCardCollection';
import MerchandisingCard from 'bundles/enterprise-collections/components/MerchandisingCard';
import { useCustomCourses } from 'bundles/enterprise-collections/context/CustomCoursesContext';
import { Heading, Section } from 'bundles/enterprise-legacy-learner-home/components/AutoHeading';
import CurriculumCollectionsQuery from 'bundles/enterprise-legacy-learner-home/components/single-program/queries/CurriculumCollections.graphql';
import type {
  CurriculumCollectionsQuery as CurriculumCollectionsQueryData,
  CurriculumCollectionsQueryVariables,
} from 'bundles/enterprise-legacy-learner-home/components/single-program/queries/__generated__/CurriculumCollections';
import type { OnProductCardClick } from 'bundles/enterprise-legacy-learner-home/types/Products';
import type {
  CollectionListItemStruct,
  Product,
  ProductType,
} from 'bundles/enterprise-legacy-learner-home/types/programCommon';
import fetchMoreErrorFilter from 'bundles/enterprise-legacy-learner-home/utils/fetchMoreErrorFilter';
import EnterpriseBadgeCollectionsContext from 'bundles/page-config-common/contexts/enterprise-skills/EnterpriseBadgeCollectionsContext';
import useCourseEntitiesAsCurriculumCourses from 'bundles/unified-home-common/hooks/useCourseEntitiesAsCurriculumCourses';

import _t from 'i18n!nls/enterprise-legacy-learner-home';

export const COLLECTION_LOAD_LIMIT = 3;

type PropsFromCaller = {
  programId: string;
  programSlug: string;
  programName?: string;
  start?: string;
  limit?: number;
  onProductCardClick: OnProductCardClick;
  title?: string;
  description?: string;
  thirdPartyOrganizationName?: string;
  enableCurriculumBuilder?: boolean;
  allowOrgForSpecializationConfiguration?: boolean;
  isGwGProgram?: boolean;
  academicDisciplineName?: string;
  isAllCollectionsPage: boolean;
};

export type Props = PropsFromCaller;

function updateCollectionQuery(
  prevResult: CurriculumCollectionsQueryData['NaptimeQueries'],
  newResult: CurriculumCollectionsQueryData['NaptimeQueries'] | undefined
) {
  if (!newResult?.ProgramCurriculumCollectionsV1Resource?.curriculumTracksByProgram) {
    return prevResult;
  }
  const elements = unionBy(
    prevResult.ProgramCurriculumCollectionsV1Resource?.curriculumTracksByProgram?.elements,
    newResult.ProgramCurriculumCollectionsV1Resource?.curriculumTracksByProgram?.elements,
    (element) => element?.id
  );
  return {
    NaptimeQueries: {
      ProgramCurriculumCollectionsV1Resource: {
        __typename: newResult.__typename,
        curriculumTracksByProgram: {
          elements,
          paging: newResult.ProgramCurriculumCollectionsV1Resource.curriculumTracksByProgram.paging,
          __typename: newResult.ProgramCurriculumCollectionsV1Resource.curriculumTracksByProgram.__typename,
        },
      },
    },
  };
}

// filter out collections that contains the word "free"
// currently only LmyL-2WZTmeRq-rWx2tDVw
const BannedCollectionIds = new Set(['LmyL-2WZTmeRq-rWx2tDVw']);

export const filterBlacklistCollections = (collections?: CollectionListItemStruct[]): CollectionListItemStruct[] => {
  return reject(collections, (collection) => BannedCollectionIds.has(collection.id));
};

export const loadMoreItems = ({
  setPaginationError,
  loading,
  fetchMore,
  data,
}: {
  setPaginationError: (v: boolean) => void;
  loading: boolean;
  data?: CurriculumCollectionsQueryData;
  fetchMore: DataValue<CurriculumCollectionsQueryData, CurriculumCollectionsQueryVariables>['fetchMore'];
}) => {
  const start = data?.NaptimeQueries?.ProgramCurriculumCollectionsV1Resource?.curriculumTracksByProgram?.paging?.next;

  if (!data || loading || !start) {
    return;
  }

  fetchMore({
    variables: {
      start,
      limit: COLLECTION_LOAD_LIMIT,
    },
    // @ts-expect-error react-apollo@2.5.4
    updateQuery: (
      { NaptimeQueries: prevResult },
      // @ts-expect-error react-apollo@2.5.4
      { fetchMoreResult: { NaptimeQueries: newResult } }
    ) => {
      return updateCollectionQuery(prevResult, newResult);
    },
  })
    .catch(fetchMoreErrorFilter)
    .catch(() => {
      setPaginationError(true);
    });
};

export function AllCuratedCollections({
  onProductCardClick,
  start = '0',
  limit = COLLECTION_LOAD_LIMIT,
  title = _t('All recommendations from your organization'),
  description,
  isGwGProgram,
  academicDisciplineName,
  programSlug,
  isAllCollectionsPage,
  allowOrgForSpecializationConfiguration,
  programId,
  programName,
  enableCurriculumBuilder,
  thirdPartyOrganizationName,
}: Props) {
  const styles = {
    refreshedTitle: css`
      margin-bottom: var(--cds-spacing-300);
    `,
    refreshedDescription: css`
      margin-top: ${4 /* XXS */ - 24 /* L */}px;
    `,
    refreshedWrapper: css`
      & .learning-path-container {
        padding: 0;
      }

      margin-top: var(--cds-spacing-200);

      ${breakpoints.down('xs')} {
        padding: 0 var(--cds-spacing-50);
        padding-top: var(--cds-spacing-300);
        margin-top: calc(var(--cds-spacing-50) * -1);
      }

      .cds-grid-item:last-child {
        ${breakpoints.down('xs')} {
          margin-top: 0;
        }
      }
    `,
    collectionContainer: css`
      section {
        padding-bottom: 0;
        ${breakpoints.down('xs')} {
          padding-top: var(--cds-spacing-300);
        }

        .cds-grid-item {
          ${breakpoints.down('xs')} {
            padding-bottom: 0;
          }
        }

        .cds-grid-item:first-child {
          ${breakpoints.down('xs')} {
            margin-top: var(--cds-spacing-50);
          }
        }

        .cds-grid-item:last-child {
          ${breakpoints.down('xs')} {
            padding-bottom: var(--cds-spacing-100);
          }
        }
      }
    `,

    merchandisingCard: css`
      ${breakpoints.up('xs')} {
        margin-top: var(--cds-spacing-400);
      }
    `,
  };

  const [paginationError, setPaginationError] = React.useState(false);
  const { enterpriseBadgesWithCollection } = React.useContext(EnterpriseBadgeCollectionsContext);
  const { privateCourses } = useCustomCourses();

  const { data, loading, error, fetchMore } = useQuery<
    CurriculumCollectionsQueryData,
    CurriculumCollectionsQueryVariables
  >(CurriculumCollectionsQuery, {
    variables: {
      programId,
      start,
      // No limit on collections for AD programs so the quickjump component has access to every collection
      limit: academicDisciplineName ? null : limit,
      enableCurriculumBuilder: Boolean(enableCurriculumBuilder),
      allowOrgForSpecializationConfiguration: Boolean(allowOrgForSpecializationConfiguration),
    },
    context: { clientName: 'gatewayGql' },
    ssr: false,
  });
  const allCourseIds =
    data?.NaptimeQueries?.ProgramCurriculumCollectionsV1Resource?.curriculumTracksByProgram?.elements?.reduce(
      (acc, collection) => {
        if (collection?.courseIds?.length) {
          acc.push(...(collection.courseIds as string[]));
        }

        return acc;
      },
      [] as string[]
    );
  const [curriculumCourseMap, curriculumCourseMapLoading] = useCourseEntitiesAsCurriculumCourses(allCourseIds);

  const collections = filterBlacklistCollections(
    data?.NaptimeQueries?.ProgramCurriculumCollectionsV1Resource?.curriculumTracksByProgram?.elements as
      CollectionListItemStruct[] | undefined
  ).map((collection) => ({
    ...collection,
    courses: {
      elements: curriculumCourseMap
        ? (collection?.courseIds?.map((id) => {
            if (curriculumCourseMap[id]) {
              return curriculumCourseMap[id];
            } else {
              return null;
            }
          }) as Array<Product | null>)
        : [],
    },
  }));
  const paging = data?.NaptimeQueries?.ProgramCurriculumCollectionsV1Resource?.curriculumTracksByProgram?.paging;
  const isAllLoaded = paging != null && paging.next == null;

  const handleProductCardClick =
    (collectionTrackingId: string | null) => (product: Product, productType: ProductType) => {
      onProductCardClick(product, productType, collectionTrackingId);
    };

  const getDescriptionToUse = () => {
    return _t(
      'Explore expertly curated collections to elevate your expertise, expand your knowledge, and boost your career growth'
    );
  };

  const getTitleToUse = () => {
    if (academicDisciplineName) {
      return _t('Academic subjects recommended in #{academicDisciplineName}', { academicDisciplineName });
    } else if (isGwGProgram) {
      return _t('Ready to jumpstart your career? Take the next step towards your future');
    } else {
      return _t('Collections selected by #{orgName}', { orgName: thirdPartyOrganizationName });
    }
  };

  const descriptionElement = (
    <Typography2 component="p" css={styles.refreshedDescription} color="supportText">
      {getDescriptionToUse()}
    </Typography2>
  );

  if (error) {
    return (
      <InlineNotification severity="error">
        {_t('Sorry! Something went wrong. Please refresh the page.')}
      </InlineNotification>
    );
  }

  if (loading || curriculumCourseMapLoading) {
    return (
      <PageGridContainer
        className="rc-AllCuratedCollections CollectionListPlaceholder"
        data-testid="collections-placeholder"
      >
        <Grid item xs={12}>
          <Heading defaultLevel={2} css={styles.refreshedTitle}>
            <Typography2 component="h1" variant="titleMedium">
              {getTitleToUse()}
            </Typography2>
          </Heading>
          <Section initialLevel={3}>
            {times(collections?.length || limit, (key) => (
              <EnterpriseProductCardCollectionsPlaceholder key={key} />
            ))}
          </Section>
        </Grid>
      </PageGridContainer>
    );
  }

  const showMerchandisingComponent = enterpriseBadgesWithCollection.length > 0 || privateCourses.length > 0;

  if (collections?.length) {
    return (
      <PageGridContainer
        className="rc-AllCuratedCollections"
        data-testid="all-curated-collections"
        css={styles.refreshedWrapper}
      >
        <Grid item xs={12}>
          <Heading defaultLevel={2} css={styles.refreshedTitle}>
            <Grid container justifyContent="space-between">
              <Typography2 component="h1" variant="titleMedium">
                {getTitleToUse()}
              </Typography2>
            </Grid>
          </Heading>
          <Section initialLevel={3}>
            {descriptionElement}
            {collections.map((collection, index) => (
              <div key={collection.id} css={styles.collectionContainer}>
                <Collection
                  collectionOrder={index + 1}
                  id={collection.id}
                  collection={collection}
                  onProductCardClick={handleProductCardClick(collection.collectionTrackingId)}
                  productMetadata={collection.programProductMetadata?.elements}
                  disableDescription={isGwGProgram}
                  allowOrgForSpecializationConfiguration={allowOrgForSpecializationConfiguration}
                />
              </div>
            ))}
            {!isAllLoaded && !paginationError && (
              // @ts-expect-error TS2769: No overload matches this call.
              <Waypoint onEnter={() => loadMoreItems({ data, loading, setPaginationError, fetchMore })}>
                <EnterpriseProductCardCollectionsPlaceholder />
              </Waypoint>
            )}
            {paginationError && (
              <InlineNotification severity="error">
                {_t('Sorry! Something went wrong. Please refresh the page.')}
              </InlineNotification>
            )}
          </Section>
          {showMerchandisingComponent && programName && (
            <Grid item xs={12} css={styles.merchandisingCard}>
              <MerchandisingCard
                programName={programName}
                subTitle={_t('Content tailored for learning at #{orgName}', { orgName: thirdPartyOrganizationName })}
                description={_t('Custom learning paths and courses to advance your career and skills')}
                redirectUrl={`/programs/${programSlug}/view-all-collections/tailored-learning`}
              />
            </Grid>
          )}
        </Grid>
      </PageGridContainer>
    );
  }

  return null;
}

export default AllCuratedCollections;
